import { Box, Button, Checkbox, Chip, FormControlLabel, Grid, Paper, Typography } from '@mui/material';
import React, { useState } from 'react';
import { DayOfWeek, TeacherAvailability } from '../../types';

interface AvailabilityEditorProps {
  availability: TeacherAvailability;
  onChange: (availability: TeacherAvailability) => void;
  maxPeriods?: number;
}

const days: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

const dayLabels: Record<DayOfWeek, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
};

export const AvailabilityEditor: React.FC<AvailabilityEditorProps> = ({ availability, onChange, maxPeriods = 5 }) => {
  const [localAvailability, setLocalAvailability] = useState<TeacherAvailability>(availability);

  const toggleDayAvailability = (day: DayOfWeek) => {
    const newAvailability = {
      ...localAvailability,
      [day]: {
        available: !localAvailability[day].available,
        periods: localAvailability[day].available ? [] : Array.from({ length: maxPeriods }, (_, i) => i + 1),
      },
    };
    setLocalAvailability(newAvailability);
    onChange(newAvailability);
  };

  const togglePeriod = (day: DayOfWeek, period: number) => {
    const dayAvail = localAvailability[day];
    const periods = dayAvail.periods.includes(period)
      ? dayAvail.periods.filter((p) => p !== period)
      : [...dayAvail.periods, period].sort((a, b) => a - b);

    const newAvailability = {
      ...localAvailability,
      [day]: {
        available: periods.length > 0,
        periods,
      },
    };
    setLocalAvailability(newAvailability);
    onChange(newAvailability);
  };

  const setAllPeriodsForDay = (day: DayOfWeek, available: boolean) => {
    const newAvailability = {
      ...localAvailability,
      [day]: {
        available,
        periods: available ? Array.from({ length: maxPeriods }, (_, i) => i + 1) : [],
      },
    };
    setLocalAvailability(newAvailability);
    onChange(newAvailability);
  };

  const setAllDaysAvailable = (available: boolean) => {
    const newAvailability: TeacherAvailability = {} as TeacherAvailability;
    days.forEach((day) => {
      newAvailability[day] = {
        available,
        periods: available ? Array.from({ length: maxPeriods }, (_, i) => i + 1) : [],
      };
    });
    setLocalAvailability(newAvailability);
    onChange(newAvailability);
  };

  const getTotalAvailablePeriods = () => {
    return Object.values(localAvailability).reduce((total, day) => total + (day.available ? day.periods.length : 0), 0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={500}>
          Teacher Availability
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={() => setAllDaysAvailable(true)}>
            Select All
          </Button>
          <Button size="small" variant="outlined" onClick={() => setAllDaysAvailable(false)}>
            Clear All
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {days.map((day) => {
            const dayAvail = localAvailability[day];
            return (
              <Grid item xs={12} key={day}>
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <FormControlLabel
                      control={<Checkbox checked={dayAvail.available} onChange={() => toggleDayAvailability(day)} />}
                      label={
                        <Typography variant="body2" fontWeight={500}>
                          {dayLabels[day]}
                        </Typography>
                      }
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setAllPeriodsForDay(day, true)}
                        disabled={!dayAvail.available}
                      >
                        All
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setAllPeriodsForDay(day, false)}
                        disabled={!dayAvail.available}
                      >
                        None
                      </Button>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      flexWrap: 'wrap',
                      pl: 4,
                      opacity: dayAvail.available ? 1 : 0.4,
                      pointerEvents: dayAvail.available ? 'auto' : 'none',
                    }}
                  >
                    {Array.from({ length: maxPeriods }, (_, i) => i + 1).map((period) => (
                      <Chip
                        key={period}
                        label={`P${period}`}
                        onClick={() => togglePeriod(day, period)}
                        color={dayAvail.periods.includes(period) ? 'primary' : 'default'}
                        variant={dayAvail.periods.includes(period) ? 'filled' : 'outlined'}
                        clickable
                        sx={{
                          minWidth: 48,
                          cursor: 'pointer',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        <Box
          sx={{
            mt: 3,
            pt: 2,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Total Available Periods
          </Typography>
          <Chip label={`${getTotalAvailablePeriods()} periods`} color="primary" variant="outlined" />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            💡 Tip: Click on individual period chips to toggle availability, or use the day checkbox to enable/disable
            all periods for that day.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};
